import { Box, Typography } from '@mui/material'
import { Route, Routes } from 'react-router'
import OnboardingTimeline from './OnboardingTimeline'
import SignupBlockCard from './SignupBlockCard'
import { ONBOARDING_STATES } from '~/entities/person'
import { SectionCard, StatusPill, type Tone } from '~/shared/ui'
import { bffCrmOnboardingTimeline } from '@fireflyhealth/api'
import { useQuery } from '~/shared/hooks/query'
import type { PatientUser } from '~/shared/types'

// current_status → header pill; an unmapped status falls through as neutral.
const STATUS_PILL: Record<string, { label: string; tone: Tone }> = {
  [ONBOARDING_STATES.initialized]: { label: 'In progress', tone: 'info' },
  [ONBOARDING_STATES.signedup]: { label: 'Signed up', tone: 'success' },
  [ONBOARDING_STATES.deactivated]: { label: 'Deactivated', tone: 'error' },
}

// Same query (and key) OnboardingTimeline runs — React Query dedupes, so the
// header pill costs no extra request.
const TimelineCardHeader = ({ personId }: { personId: number }) => {
  const { data } = useQuery(
    bffCrmOnboardingTimeline.retrieveQueryKey({ person_id: personId }),
    () => bffCrmOnboardingTimeline.retrieve({ person_id: personId }).then(r => r.data),
    { staleTime: 0, refetchOnMount: 'always', enabled: !!personId },
  )
  const pill = data?.current_status
    ? (STATUS_PILL[data.current_status] ?? { label: data.current_status, tone: 'neutral' })
    : null
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: 'text.primary' }}>
        Onboarding Progress
      </Typography>
      {pill && <StatusPill tone={pill.tone} label={pill.label} />}
    </Box>
  )
}

/**
 * Design-refresh Onboarding tab: the token-driven OnboardingTimeline rail in a
 * SectionCard, above it the ops signup-block card.
 */
const PatientDetailOnboarding = (props: { hidden?: boolean; patient: PatientUser }) => {
  const { hidden, patient } = props
  if (hidden) return null

  const personId = patient.person.id
  return (
    <Box>
      <Routes>
        <Route
          index
          element={
            <>
              <SignupBlockCard personId={personId} userId={patient?.id ?? null} />
              <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
                <SectionCard>
                  <TimelineCardHeader personId={personId} />
                  <OnboardingTimeline personId={personId} hideChrome />
                </SectionCard>
              </Box>
            </>
          }
        />
      </Routes>
    </Box>
  )
}

export default PatientDetailOnboarding
